import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { loadStripe } from '@stripe/stripe-js';

interface SubscriptionInfo {
  organization_id: string;
  organization_name: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  subscription_plan: string | null;
  subscription_period_start: string | null;
  subscription_period_end: string | null;
  subscription_cancel_at_period_end: boolean;
  subscription_canceled_at: string | null;
  trial_mode: boolean;
  trial_expires_at: string | null;
  asset_limit: number | null;
  current_asset_count: number;
  user_limit: number | null;
  custom_field_limit: number | null;
  scheduled_report_limit: number | null;
  priority_support: number | null;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  price: {
    id: string;
    unit_amount: number;
    currency: string;
    recurring: {
      interval: string;
      interval_count: number;
    } | null;
  };
}

// Initialize Stripe (use your publishable key)
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

/**
 * Fetch subscription information for the current organization
 * Only accessible by admins
 */
export function useSubscription(organizationId?: string) {
  return useQuery({
    queryKey: ['subscription', organizationId],
    queryFn: async (): Promise<SubscriptionInfo | null> => {
      if (!organizationId) return null;

      const { data, error } = await supabase
        .from('organization_subscription_info')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error) {
        console.error('Error fetching subscription:', error);
        throw error;
      }

      return data;
    },
    enabled: !!organizationId,
  });
}

/**
 * Create a Stripe checkout session to start a new subscription
 */
export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: async ({
      priceId,
      organizationId
    }: {
      priceId: string;
      organizationId: string;
    }): Promise<{ sessionId: string }> => {
      // Call your backend API to create a Stripe checkout session
      // This should be implemented as a Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: { priceId, organizationId },
      });

      if (error) throw error;

      return data;
    },
    onSuccess: async (data) => {
      // Redirect to Stripe checkout
      const stripe = await stripePromise;
      if (stripe) {
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      }
    },
  });
}

/**
 * Create a Stripe billing portal session for subscription management
 */
export function useCreatePortalSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string): Promise<{ url: string }> => {
      // Call your backend API to create a Stripe portal session
      // This should be implemented as a Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { organizationId },
      });

      if (error) throw error;

      return data;
    },
    onSuccess: (data) => {
      // Redirect to Stripe billing portal
      window.location.href = data.url;
    },
    onSettled: (_, __, organizationId) => {
      // Invalidate subscription query when user returns from portal
      queryClient.invalidateQueries({ queryKey: ['subscription', organizationId] });
    },
  });
}

/**
 * Cancel subscription at period end
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string): Promise<void> => {
      // Call your backend API to cancel subscription
      const { error } = await supabase.functions.invoke('cancel-subscription', {
        body: { organizationId },
      });

      if (error) throw error;
    },
    onSuccess: (_, organizationId) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', organizationId] });
    },
  });
}

/**
 * Reactivate a canceled subscription
 */
export function useReactivateSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string): Promise<void> => {
      // Call your backend API to reactivate subscription
      const { error } = await supabase.functions.invoke('reactivate-subscription', {
        body: { organizationId },
      });

      if (error) throw error;
    },
    onSuccess: (_, organizationId) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', organizationId] });
    },
  });
}

/**
 * Create a Stripe customer for an organization
 * Should be called after organization creation
 */
export function useCreateStripeCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationId: string): Promise<{ customerId: string }> => {
      const { data, error } = await supabase.functions.invoke('create-stripe-customer', {
        body: { organizationId },
      });

      if (error) throw error;

      return data;
    },
    onSuccess: (_, organizationId) => {
      queryClient.invalidateQueries({ queryKey: ['subscription', organizationId] });
    },
  });
}

/**
 * Fetch available subscription plans from Stripe
 */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async (): Promise<StripeProduct[]> => {
      // Fetch available products/prices from your backend
      // This should be implemented as a Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('get-subscription-plans');

      if (error) {
        console.error('Error fetching subscription plans:', error);
        throw error;
      }

      return data.plans || [];
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });
}
